import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileStack, Users, Building2, MapPin, Package, Truck, LogOut, Tags, Activity, Inbox, Route as RouteIcon, ClipboardCheck, TrendingUp, CalendarDays, HardHat, ClipboardList, BarChart3, Receipt, Target, CheckSquare, Clock, Palmtree, Briefcase, FileText, ChevronDown, Settings, Wallet, Hammer, MessageCircle, BookOpen, Boxes, Menu, X as IconX, Sparkles, ShieldCheck, ShieldAlert, Satellite, ScanLine, Image as ImageIcon, Heart, Banknote, AlertTriangle, PanelLeftClose, PanelLeftOpen, Star, ShoppingCart, Megaphone, PieChart, ListChecks, Wrench, RefreshCcw, Palette, PackageCheck, FileSpreadsheet, KeyRound, Bell, ArrowRightLeft, Sheet,   Map as MapIcon, MailWarning, ShoppingBag, Sticker, FileSignature,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ROLE_LABELS } from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";
import { useBadgesPolling } from "@/hooks/useBadgesPolling";
import DnfLogo from "@/components/DnfLogo";
import GlobalSearch from "@/components/GlobalSearch";
import SidebarSearch from "@/components/SidebarSearch";
import AIChatBubble from "@/components/AIChatBubble";
import ManualButton from "@/components/ManualButton";
import WelcomeDialog from "@/components/WelcomeDialog";

// Items "Inicio" — sueltos arriba sin grupo (Dashboard + atajos personales)
const NAV_INICIO = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/mi-dia", label: "Mi Día", icon: Sparkles },
  { to: "/mis-pendientes", label: "Mis pendientes", icon: Inbox },
  { to: "/tareas", label: "Tareas de equipo", icon: CheckSquare, badgeKey: "tareas_pendientes" },
  { to: "/peticiones/bandeja-ia", label: "Asistente IA", icon: Sparkles, roles: ["admin", "comercial", "produccion", "administracion"], badgeKey: "peticiones_ia_pendientes" },
  { to: "/peticiones/correo-entrante", label: "Correo entrante", icon: Inbox, roles: ["admin", "comercial", "produccion", "administracion"] },
  { to: "/contrataciones", label: "Contrataciones cliente", icon: FileSignature, roles: ["admin", "comercial", "administracion"] },
];

// Grupos colapsables — orden alineado con la propuesta del Excel
const NAV_GROUPS = [
  {
    key: "crm",
    label: "CRM & Ventas",
    icon: Target,
    items: [
      { to: "/leads", label: "Leads", icon: Target, roles: ["admin", "comercial"], badgeKey: "leads_pendientes" },
      { to: "/presupuestos", label: "Presupuestos", icon: FileText, roles: ["admin", "comercial"] },
      { to: "/whatsapp", label: "Comunicaciones", icon: MessageCircle, roles: ["admin", "comercial"] },
      { to: "/aprobaciones-pendientes", label: "Aprobaciones", icon: ClipboardList, roles: ["admin", "comercial"] },
      { to: "/clientes", label: "Directorio Clientes", icon: Building2, roles: ["admin", "comercial", "produccion", "logistica", "administracion", "gestoria"] },
      { to: "/cadenas", label: "Cadenas Comerciales", icon: Tags, roles: ["admin", "comercial", "produccion", "logistica"] },
      { to: "/espacios", label: "Gestión de Espacios", icon: MapPin, roles: ["admin", "comercial", "produccion", "logistica"] },
      { to: "/sub-clientes", label: "Portales Externos", icon: Briefcase, adminOnly: true },
      { to: "/catalogo-precios", label: "Tarifario", icon: Tags, roles: ["admin", "comercial"] },
      { to: "/feedback", label: "Calidad y Feedback", icon: Heart, roles: ["admin", "comercial", "produccion"] },
    ],
  },
  {
    key: "produccion",
    label: "Producción",
    icon: Hammer,
    items: [
      { to: "/produccion", label: "Diseño y producción", alias: ["Mi Producción", "Elia", "Kanban"], icon: Palette, roles: ["admin", "diseño", "diseno", "produccion"] },
      { to: "/produccion/calculadora-pegatinas", label: "🏷️ Calculadora Pegatinas", icon: Sticker, roles: ["admin", "diseño", "diseno", "produccion"] },
      { to: "/peticiones", label: "Solicitudes de trabajo", alias: ["Peticiones de Taller", "Peticiones"], icon: FileStack, roles: ["admin", "comercial", "produccion", "logistica", "diseño"] },
      { to: "/validaciones", label: "Control de Calidad", icon: ShieldCheck, adminOnly: true, badgeKey: "validaciones_pendientes" },
      { to: "/supervision", label: "Supervisión de Planta", icon: Activity, roles: ["admin", "produccion", "logistica"] },
    ],
  },
  {
    key: "compras",
    label: "Compras & Inventario",
    icon: ShoppingCart,
    items: [
      { to: "/materiales", label: "Catálogo de Materiales", icon: Package, roles: ["admin", "produccion", "logistica", "diseño"] },
      { to: "/almacen", label: "Control de Almacén", icon: Boxes, roles: ["admin", "produccion", "logistica"] },
      { to: "/proveedores", label: "Directorio Proveedores", icon: Truck, roles: ["admin", "produccion", "logistica", "administracion"] },
    ],
  },
  {
    key: "logistica",
    label: "Logística e Instalaciones",
    icon: Truck,
    items: [
      { to: "/logistica-entrada", label: "Materiales de montaje", alias: ["Mi Logística", "Sara", "Recepción de material"], icon: PackageCheck, roles: ["admin", "logistica", "administracion"], permisoAlt: "logistica:recepcion" },
      { to: "/mi-ruta", label: "🚐 Mi Ruta de Hoy", icon: RouteIcon, roles: ["admin", "montador", "montador_externo", "logistica"] },
      { to: "/admin/aprobaciones-rutas", label: "⚠ Aprobaciones rutas", icon: ShieldAlert, permiso: "rutas:autorizar_salida_incompleta" },
      { to: "/pendientes-montar", label: "🏗 Pendientes de montar", icon: Package, roles: ["admin", "logistica", "administracion", "produccion"] },
      { to: "/cronograma", label: "📅 Cronograma", icon: CalendarDays, roles: ["admin", "logistica", "administracion", "produccion"] },
      { to: "/rutas", label: "Planificación de Rutas", icon: RouteIcon, roles: ["admin", "logistica", "produccion"] },
      { to: "/reparto-inteligente", label: "Reparto Inteligente", icon: Sparkles, roles: ["admin", "logistica", "produccion"] },
      { to: "/calendario", label: "Calendario de Montajes", icon: CalendarDays, roles: ["admin", "logistica", "produccion", "comercial"] },
      { to: "/montajes-tabla", label: "📋 Montajes (Tabla)", icon: Sheet, roles: ["admin", "logistica", "administracion", "produccion", "gestoria"] },
      { to: "/r", label: "Reporte en Campo (QR)", icon: ScanLine, roles: ["admin", "logistica", "produccion", "montador_externo"] },
      { to: "/vehiculos", label: "Gestión de Vehículos", icon: Truck, roles: ["admin", "logistica"] },
      { to: "/mantenimiento", label: "Mantenimiento y Uso", icon: Wrench, roles: ["admin", "logistica"], badgeKey: "flota_alertas" },
      { to: "/flota-mapa", label: "Seguimiento Flota (Mapa)", icon: Satellite, roles: ["admin", "logistica"] },
      { to: "/flota-dashboard", label: "Dashboard de Flota", icon: BarChart3, roles: ["admin", "logistica"] },
      { to: "/albaranes", label: "Albaranes de Entrega", icon: Truck, adminOnly: true },
      { to: "/fotos-pendientes", label: "Evidencias y Fotos", icon: ImageIcon, roles: ["admin", "gestoria", "administracion", "produccion", "logistica", "comercial", "diseño"], badgeKey: "fotos_pendientes" },
    ],
  },
  {
    key: "finanzas",
    label: "Finanzas",
    icon: Wallet,
    items: [
      { to: "/facturas", label: "Facturación (Emitidas)", icon: Receipt, adminOnly: true },
      { to: "/facturas-externos", label: "Facturación (Recibidas)", icon: FileText, roles: ["admin", "gestoria", "montador_externo"] },
      { to: "/cierres-mensuales", label: "Cierres de Mes", icon: ClipboardCheck, adminOnly: true },
      { to: "/tesoreria", label: "Tesorería y Caja", icon: Banknote, roles: ["admin", "administracion", "comercial"] },
      { to: "/gastos", label: "Gastos y Compras", icon: Receipt },
      { to: "/gestoria/inmatic", label: "Enlace Gestoría", icon: FileText, roles: ["admin", "gestoria"] },
      { to: "/inversion-personal", label: "Inversiones", icon: Wallet, roles: ["admin", "gestoria"] },
    ],
  },
  {
    key: "rrhh",
    label: "Recursos Humanos",
    icon: Palmtree,
    items: [
      { to: "/fichaje", label: "Fichaje", icon: Clock },
      { to: "/admin/fichajes", label: "Control de Presencia", icon: Clock, roles: ["admin", "gestoria"] },
      { to: "/rrhh", label: "Portal del empleado", icon: Palmtree },
      { to: "/admin/rrhh", label: "Administración Laboral", icon: Briefcase, roles: ["admin", "gestoria"] },
      { to: "/admin/carga-trabajo", label: "Carga de Trabajo", icon: BarChart3, roles: ["admin", "gestoria"] },
    ],
  },
  {
    key: "analitica",
    label: "Analítica Global",
    icon: PieChart,
    items: [
      { to: "/reportes", label: "Reportes Operativos", icon: ClipboardCheck, roles: ["admin", "produccion", "logistica", "comercial"], badgeKey: "reportes_pendientes" },
      { to: "/no-realizadas", label: "No realizadas (decisión)", icon: ClipboardCheck, roles: ["admin", "logistica"] },
      { to: "/productividad", label: "Productividad", icon: TrendingUp, roles: ["admin", "produccion", "logistica", "comercial"] },
      { to: "/estadisticas", label: "Estadísticas", icon: BarChart3, adminOnly: true },
      { to: "/rentabilidad", label: "Rentabilidad Financiera", icon: TrendingUp, adminOnly: true },
    ],
  },
  {
    key: "config",
    label: "Configuración del Sistema",
    icon: Settings,
    items: [
      { to: "/usuarios", label: "Usuarios y Permisos", icon: Users, adminOnly: true },
      { to: "/admin/aprendizaje-parser", label: "Aprendizaje IA correos", icon: Sparkles, adminOnly: true },
      { to: "/docs-internos", label: "Base de Conocimiento", icon: BookOpen, adminOnly: true },
      { to: "/admin/migracion-v2", label: "Migración Flujo v2", icon: RefreshCcw, adminOnly: true },
      { to: "/admin/marco-vinilo", label: "Migrar Marcos Vinilo", icon: Hammer, adminOnly: true },
      { to: "/admin/portfolio", label: "Portfolio Público", icon: ImageIcon, adminOnly: true },
      { to: "/admin/diag-planificacion", label: "Diag. Planificación", icon: FileSpreadsheet, adminOnly: true },
      { to: "/admin/normalizar-entregado", label: "Normalizar 'entregado'", icon: ArrowRightLeft, adminOnly: true },
      { to: "/admin/normalizacion", label: "Normalización y Limbo IA", icon: MailWarning, adminOnly: true },
      { to: "/admin/diag-logistica", label: "Diag. Logística", icon: PackageCheck, adminOnly: true },
      { to: "/admin/diag-whatsapp", label: "Diag. WhatsApp", icon: MessageCircle, adminOnly: true },
      { to: "/admin/diag", label: "Diagnóstico Técnico", icon: AlertTriangle, adminOnly: true, badgeKey: "diag_errores" },
      { to: "/admin/health-checks", label: "Health Checks", icon: ShieldCheck, adminOnly: true, badgeKey: "hc_criticals" },
      { to: "/admin/integraciones", label: "Integraciones", icon: Activity, adminOnly: true },
      { to: "/admin/api-keys", label: "API Keys", icon: KeyRound, adminOnly: true },
      { to: "/admin/notificaciones", label: "Notificaciones", icon: Bell, adminOnly: true },
      { to: "/admin/roadmap", label: "Roadmap & Docs", icon: MapIcon, adminOnly: true },
      { to: "/admin/importar", label: "Importar Excel", icon: FileSpreadsheet, adminOnly: true },
      { to: "/admin/tienda-pegatinas", label: "Tienda online (pegatinas)", icon: ShoppingBag, adminOnly: true },
      { to: "/manuales", label: "Manuales de Usuario", icon: BookOpen },
    ],
  },
];

// Paths visibles a usuario SÓLO gestoría
const GESTORIA_PATHS = new Set([
  "/", "/fichaje", "/admin/fichajes", "/rrhh", "/admin/rrhh", "/admin/carga-trabajo", "/facturas-externos", "/gestoria/inmatic", "/inversion-personal", "/manuales",
]);

// Paths visibles a un MONTADOR (interno) — sin acceso a CRM, peticiones, materiales, etc.
const MONTADOR_PATHS = new Set([
  "/", "/fichaje", "/rrhh", "/gastos", "/manuales",
]);

// Paths visibles a un COLABORADOR EXTERNO / AUTÓNOMO — sólo lo suyo.
const MONTADOR_EXTERNO_PATHS = new Set([
  "/", "/fichaje", "/rrhh", "/gastos", "/facturas-externos", "/manuales",
]);

const LS_KEY_OPEN_GROUPS = "dnf:sidebar:open-groups";
const LS_KEY_COLLAPSED = "dnf:sidebar:collapsed";
const LS_KEY_FAVS = "dnf:sidebar:favoritos";    // per-user → ":<userId>"
const LS_KEY_FAVS_OPEN = "dnf:sidebar:favoritos-open";

// Flatten todos los items para resolver favoritos por `to`
const ALL_ITEMS = [
  ...NAV_INICIO,
  ...NAV_GROUPS.flatMap((g) => g.items),
];
function getItemByPath(path) {
  return ALL_ITEMS.find((it) => it.to === path);
}

export default function AppLayout() {
  const { user, logout, hasRole, hasPermission } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Sidebar collapsed (desktop only) — persiste en localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(LS_KEY_COLLAPSED) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY_COLLAPSED, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  // Estado open/closed por grupo, persistido por usuario en localStorage
  const lsKey = user?.id ? `${LS_KEY_OPEN_GROUPS}:${user.id}` : LS_KEY_OPEN_GROUPS;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey) || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem(lsKey, JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups, lsKey]);

  // ⭐ Favoritos del sidebar — persistido por usuario en localStorage
  const favsKey = user?.id ? `${LS_KEY_FAVS}:${user.id}` : LS_KEY_FAVS;
  const [favoritos, setFavoritos] = useState(() => {
    try {
      const raw = localStorage.getItem(favsKey);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(favsKey, JSON.stringify(favoritos)); } catch { /* ignore */ }
  }, [favoritos, favsKey]);
  // Recargar favoritos cuando cambia el usuario logueado
  useEffect(() => {
    try {
      const raw = localStorage.getItem(favsKey);
      setFavoritos(raw ? JSON.parse(raw) : []);
    } catch { setFavoritos([]); }
  }, [favsKey]);

  const [favsOpen, setFavsOpen] = useState(() => {
    try { return localStorage.getItem(LS_KEY_FAVS_OPEN) !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY_FAVS_OPEN, favsOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [favsOpen]);

  const toggleFav = useCallback((toPath) => {
    setFavoritos((arr) => arr.includes(toPath) ? arr.filter((p) => p !== toPath) : [...arr, toPath]);
  }, []);

  // (debe ir DESPUÉS de las declaraciones isOnlyExterno/Montador/Gestoria)
  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));  // Si el usuario es SOLO gestoria, restringir la nav
  const isOnlyGestoria = useMemo(() => {
    const roles = user?.roles || [];
    return roles.includes("gestoria") && !roles.includes("admin");
  }, [user?.roles?.join(",")]);

  // Si SOLO es montador (no admin/comercial/etc), whitelist estricta
  const isOnlyMontador = useMemo(() => {
    const roles = user?.roles || [];
    if (!roles.includes("montador")) return false;
    return !["admin", "comercial", "produccion", "logistica", "diseño", "administracion", "gestoria"].some((r) => roles.includes(r));
  }, [user?.roles?.join(",")]);

  // Si SOLO es montador externo / autónomo, whitelist con facturas externos
  const isOnlyExterno = useMemo(() => {
    const roles = user?.roles || [];
    if (!roles.includes("montador_externo")) return false;
    return !["admin", "comercial", "produccion", "logistica", "diseño", "administracion", "gestoria", "montador"].some((r) => roles.includes(r));
  }, [user?.roles?.join(",")]);

  const itemVisible = useCallback((n) => {
    if (n.permisoAlt && hasPermission(n.permisoAlt)) return true;
    if (isOnlyExterno) return MONTADOR_EXTERNO_PATHS.has(n.to);
    if (isOnlyMontador) return MONTADOR_PATHS.has(n.to);
    if (isOnlyGestoria) return GESTORIA_PATHS.has(n.to);
    if (n.adminOnly) return hasRole("admin");
    if (n.permiso) return hasPermission(n.permiso);
    if (n.roles) return n.roles.some((r) => hasRole(r));
    return true;
  }, [hasRole, hasPermission, isOnlyGestoria, isOnlyMontador, isOnlyExterno]);

  // Bloqueo de rutas: si el usuario está restringido y trata de ir a una ruta no permitida → home
  useEffect(() => {
    if (!user || !loc.pathname) return;
    const allowed = isOnlyExterno ? MONTADOR_EXTERNO_PATHS
                   : isOnlyMontador ? MONTADOR_PATHS
                   : isOnlyGestoria ? GESTORIA_PATHS
                   : null;
    if (!allowed) return;
    const path = loc.pathname;
    if (path === "/perfil") return;
    if (path.startsWith("/manuales/")) return;
    if (path === "/mi-ruta") return;
    if (path === "/logistica-entrada" && hasPermission("logistica:recepcion")) return;
    if (allowed.has(path)) return;
    nav("/", { replace: true });
  }, [loc.pathname, user, isOnlyExterno, isOnlyMontador, isOnlyGestoria, nav, hasPermission]);

  // Items de Inicio visibles
  const visibleInicio = useMemo(
    () => NAV_INICIO.filter(itemVisible),
    [itemVisible, user?.roles?.join(",")],
  );

  // Grupos visibles (sólo si tienen al menos 1 item visible)
  const visibleGroups = useMemo(() => {
    return NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(itemVisible) }))
      .filter((g) => g.items.length > 0);
  }, [itemVisible, user?.roles?.join(",")]);

  // Lista plana de items para buscador Ctrl/⌘+K (con grupo etiquetado)
  const searchItems = useMemo(() => {
    const flat = [];
    visibleInicio.forEach((it) => flat.push({ ...it, group: "Inicio" }));
    visibleGroups.forEach((g) => g.items.forEach((it) => flat.push({ ...it, group: g.label })));
    return flat;
  }, [visibleInicio, visibleGroups]);

  // Auto-expandir el grupo que contiene la ruta activa
  useEffect(() => {
    for (const g of visibleGroups) {
      const active = g.items.some((it) => {
        if (it.end) return loc.pathname === it.to;
        return loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
      });
      if (active && !openGroups[g.key]) {
        setOpenGroups((s) => ({ ...s, [g.key]: true }));
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, visibleGroups.length]);

  const badges = useBadgesPolling(user, hasRole);

  // Suma de badges de un grupo (para mostrar puntito ámbar en cabecera cerrada)
  const groupBadgeCount = (g) =>
    g.items.reduce((sum, it) => sum + (it.badgeKey ? (badges[it.badgeKey] || 0) : 0), 0);

  // Sidebar móvil (drawer)
  const [mobileOpen, setMobileOpen] = useState(false);
  // Cerrar el drawer cuando cambia la ruta (usa el `loc` ya disponible arriba)
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0a0a0c] text-zinc-100" data-testid="app-layout">
      {/* Overlay móvil */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden"
             onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`shrink-0 border-r border-zinc-800/70 bg-[#0c0c10] flex flex-col
                         fixed md:static inset-y-0 left-0 z-50 h-[100dvh] md:h-auto md:min-h-screen
                         transition-[transform,width] duration-200 md:translate-x-0
                         w-64 ${collapsed ? "md:w-16" : "md:w-64"}
                         ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className={`px-5 py-5 border-b border-zinc-800/70 flex items-center gap-3 ${collapsed ? "md:px-3 md:justify-center" : ""}`}>
          <DnfLogo size={collapsed ? 36 : 44} />
          {!collapsed && (
            <div className="flex-1 md:block">
              <div className="text-sm font-bold tracking-wide">RotulaciónDNF</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Producción Gráfica</div>
            </div>
          )}
          <button onClick={() => setMobileOpen(false)}
                  className="md:hidden text-zinc-500 hover:text-zinc-100"
                  data-testid="sidebar-close">
            <IconX className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-3 overflow-y-auto">
          {/* Buscador rápido — Ctrl/⌘+K */}
          {!collapsed && (
            <SidebarSearch items={searchItems} />
          )}

          {/* INICIO — sin grupo */}
          {visibleInicio.length > 0 && (
            <div className="space-y-0.5" data-testid="nav-inicio">
              {visibleInicio.map((n) => (
                <NavItem key={n.to} item={n} badges={badges} collapsed={collapsed}
                  isFav={favoritos.includes(n.to)} onToggleFav={toggleFav} />
              ))}
            </div>
          )}

          {/* ⭐ FAVORITOS — solo si hay al menos uno */}
          {(() => {
            const favItems = favoritos
              .map(getItemByPath)
              .filter(Boolean)
              .filter((it) => {
                // Aplicar mismos filtros de visibilidad que el resto
                if (it.adminOnly && !hasRole("admin")) return false;
                if (it.permiso && !hasPermission(it.permiso)) return false;
                if (it.roles && !it.roles.some((r) => hasRole(r))) return false;
                return true;
              });
            if (favItems.length === 0) return null;
            if (collapsed) {
              return (
                <div data-testid="nav-favoritos" className="space-y-0.5">
                  {favItems.map((n) => <NavItem key={n.to} item={n} badges={badges} collapsed={collapsed}
                    isFav onToggleFav={toggleFav} />)}
                  <div className="my-2 mx-3 border-b border-zinc-800/40" />
                </div>
              );
            }
            return (
              <div data-testid="nav-favoritos">
                <button
                  type="button"
                  onClick={() => setFavsOpen((v) => !v)}
                  data-testid="nav-favoritos-toggle"
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[10px] uppercase tracking-[0.18em] text-amber-400/70 hover:text-amber-300 hover:bg-amber-500/5 transition-colors"
                >
                  <Star className="w-3.5 h-3.5 fill-amber-400/30" />
                  <span className="flex-1 text-left">Favoritos · {favItems.length}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${favsOpen ? "rotate-0" : "-rotate-90"}`} />
                </button>
                {favsOpen && (
                  <div className="mt-0.5 ml-1.5 pl-2 border-l border-amber-500/30 space-y-0.5">
                    {favItems.map((n) => (
                      <NavItem key={n.to} item={n} badges={badges} collapsed={collapsed}
                        isFav onToggleFav={toggleFav} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* GRUPOS colapsables */}
          {visibleGroups.map((g) => {
            const open = !!openGroups[g.key];
            const totalBadge = groupBadgeCount(g);
            const GroupIcon = g.icon;
            // Cuando el sidebar está colapsado en desktop: pintar grupos como
            // lista plana de iconos (sin header de grupo)
            if (collapsed) {
              return (
                <div key={g.key} data-testid={`nav-group-${g.key}`} className="space-y-0.5">
                  {g.items.map((n) => <NavItem key={n.to} item={n} badges={badges} collapsed={collapsed}
                    isFav={favoritos.includes(n.to)} onToggleFav={toggleFav} />)}
                  <div className="my-2 mx-3 border-b border-zinc-800/40" />
                </div>
              );
            }
            return (
              <div key={g.key} data-testid={`nav-group-${g.key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  data-testid={`nav-group-toggle-${g.key}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/40 transition-colors group"
                >
                  <GroupIcon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left">{g.label}</span>
                  {!open && totalBadge > 0 && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"
                      data-testid={`nav-group-dot-${g.key}`} />
                  )}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
                  />
                </button>
                {open && (
                  <div className="mt-0.5 ml-1.5 pl-2 border-l border-zinc-800/60 space-y-0.5">
                    {g.items.map((n) => <NavItem key={n.to} item={n} badges={badges} collapsed={collapsed}
                      isFav={favoritos.includes(n.to)} onToggleFav={toggleFav} />)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={`p-3 border-t border-zinc-800/70 ${collapsed ? "md:px-2" : ""}`}>
          {collapsed ? (
            <>
              <NavLink to="/perfil" data-testid="perfil-link"
                title={user?.name}
                className="flex items-center justify-center w-10 h-10 mx-auto rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition-colors mb-2 text-sm font-bold">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </NavLink>
              <Button
                variant="outline"
                size="sm"
                title="Salir"
                aria-label="Salir"
                className="w-full bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-300 px-0"
                onClick={async () => { await logout(); nav("/login"); }}
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <>
              <NavLink to="/perfil" data-testid="perfil-link"
                className="block px-3 py-2 rounded-md bg-zinc-900/60 mb-2 hover:bg-zinc-900/90 transition-colors group">
                <div className="text-sm font-semibold truncate group-hover:text-amber-300" data-testid="current-user-name">{user?.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
                <div className="text-[10px] mt-1 text-amber-300/80">
                  {(user?.roles || []).map((r) => ROLE_LABELS[r] || r).join(" · ")}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1 italic">Mi perfil · cambiar contraseña →</div>
              </NavLink>
              <NavLink to="/mis-acreditaciones" data-testid="mis-acreditaciones-link"
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 mb-2 hover:bg-amber-500/20 transition-colors text-amber-200 text-xs font-medium">
                <span>🪪</span> Mis acreditaciones
              </NavLink>
              <div className="grid grid-cols-1 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-transparent border-zinc-700 hover:bg-zinc-900 text-zinc-300 text-xs"
                  onClick={async () => { await logout(); nav("/login"); }}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" /> Salir
                </Button>
              </div>
            </>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto md:ml-0">
        <div className="sticky top-0 z-40 flex items-center justify-between gap-2 px-4 md:px-6 py-3 bg-[#0a0a0c]/80 backdrop-blur border-b border-zinc-800/60">
          <button onClick={() => setMobileOpen(true)}
                  className="md:hidden inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-zinc-700 bg-zinc-900/70 hover:bg-zinc-800 text-zinc-100 text-sm font-medium"
                  data-testid="sidebar-hamburger"
                  aria-label="Abrir menú">
            <Menu className="w-4 h-4" /> Menú
          </button>
          <button onClick={() => setCollapsed((c) => !c)}
                  className="hidden md:flex items-center justify-center w-9 h-9 rounded-md hover:bg-zinc-900/70 text-zinc-400 hover:text-amber-300 transition-colors"
                  data-testid="sidebar-collapse-toggle"
                  title={collapsed ? "Expandir menú" : "Colapsar menú (más espacio de trabajo)"}
                  aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}>
            {collapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
          <div className="flex-1 md:hidden text-sm font-bold tracking-wide">RotulaciónDNF</div>
          <div className="hidden md:flex flex-1" />
          <NotificationsBell />
        </div>
        <div className="px-4 md:px-0">
          <Outlet />
        </div>
      </main>
      <GlobalSearch navItems={searchItems} />
      <AIChatBubble />
      <ManualButton />
      <WelcomeDialog />
    </div>
  );
}


function NavItem({ item, badges, collapsed, isFav, onToggleFav }) {
  const Icon = item.icon;
  const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.label.toLowerCase()}`}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 ${collapsed ? "md:px-2 md:justify-center" : "px-3"} py-2 rounded-md text-sm transition-colors ${
          isActive
            ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
            : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/70 border border-transparent"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {badgeCount > 0 && !collapsed && (
        <span data-testid={`nav-badge-${item.label.toLowerCase()}`}
          className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
          {badgeCount}
        </span>
      )}
      {badgeCount > 0 && collapsed && (
        <span data-testid={`nav-badge-${item.label.toLowerCase()}`}
          className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
      )}
      {/* ⭐ Botón estrella — solo visible al hacer hover (o si ya es favorito), no colapsado */}
      {!collapsed && onToggleFav && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(item.to); }}
          data-testid={`nav-fav-${item.label.toLowerCase()}`}
          aria-label={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
          title={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
          className={`shrink-0 p-0.5 rounded transition-opacity ${
            isFav ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
        >
          <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-400 text-amber-400" : "text-zinc-500 hover:text-amber-300"}`} />
        </button>
      )}
    </NavLink>
  );
}
